import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaClipboardList } from "react-icons/fa";
import moment from "moment";
import styles from "../Create.module.css";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import { CustomButton, CustomTextInput } from "../../../../components";
import PopupModal from "../../../../components/PopupModal/PopupModal";

const AdmissionCycleConfigView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { translations } = useSelector((state) => state.language);
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const staff_id = clientData?.master_id || "";

  const cycleId = location.state?.id || "";

  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [formData, setFormData] = useState({
    academicYear: "",
    admissionType: "",
    admissionName: "",
    admissionStartDate: "",
    admissionEndDate: "",
    screeningStartDate: "",
    screeningEndDate: "",
    interviewStartDate: "",
    interviewEndDate: "",
    completionDate: "",
  });

  // Modal state: action differentiates confirm vs result
  const [modalState, setModalState] = useState({
    open: false,
    type: "success",
    message: "",
    action: "", // "deleteConfirm" | "deleteSuccess" | "deleteError" | "fetchError"
  });

  useEffect(() => {
    if (cycleId) {
      fetchAdmissionCycleView();
    }
  }, [cycleId]);

  // Format ISO date to DD/MM/YYYY for display
  const formatDisplayDate = (date) => {
    if (!date) return "";
    return moment(date).format("DD/MM/YYYY");
  };

  // Fetch view data
  const fetchAdmissionCycleView = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.post(
        "/admission/viewAdmissionCycle",
        { id: Number(cycleId) },
      );

      if (response?.data?.status) {
        const data = response.data.data;
        setFormData({
          academicYear: data.academic_year || "",
          admissionType: data.admission_type || "",
          admissionName: data.name || "",
          admissionStartDate: formatDisplayDate(data.start_date),
          admissionEndDate: formatDisplayDate(data.end_date),
          screeningStartDate: formatDisplayDate(data.screening_start_date),
          screeningEndDate: formatDisplayDate(data.screening_end_date),
          interviewStartDate: formatDisplayDate(data.interview_start_date),
          interviewEndDate: formatDisplayDate(data.interview_end_date),
          completionDate: formatDisplayDate(data.completion_date),
        });
      }
    } catch (error) {
      console.error("Error fetching admission cycle view:", error);
      setModalState({
        open: true,
        type: "error",
        message:
          error?.response?.data?.message ||
          translations.labels?.fetchFailed ||
          "Failed to fetch details. Please try again.",
        action: "fetchError",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle Edit
  const handleEdit = () => {
    navigate("/user/Admission-Cycle-Config-Create", {
      state: { id: cycleId },
    });
  };

  // Step 1: Show delete confirmation modal
  const handleDelete = () => {
    setModalState({
      open: true,
      type: translations.labels?.delete || "Delete",
      message:
        translations.labels?.confirmDelete || "Are you sure you want to delete?",
      action: "deleteConfirm",
    });
  };

  // Step 2: Execute delete API
  const executeDelete = async () => {
    setDeleting(true);
    try {
      const response = await AxiosInstance.post(
        "/admission/deleteAdmissionCycle",
        {
          id: Number(cycleId),
          deleted_by: Number(staff_id),
        },
      );

      if (response?.data?.status) {
        setModalState({
          open: true,
          type: "success",
          message:
            response.data?.message ||
            translations.labels?.deleteSuccess ||
            "Deleted successfully!",
          action: "deleteSuccess",
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message:
            response.data?.message ||
            translations.labels?.deleteFailed ||
            "Failed to delete.",
          action: "deleteError",
        });
      }
    } catch (error) {
      console.error("Error deleting admission cycle:", error);
      setModalState({
        open: true,
        type: "error",
        message:
          error?.response?.data?.message ||
          translations.labels?.deleteFailed ||
          "Failed to delete.",
        action: "deleteError",
      });
    } finally {
      setDeleting(false);
    }
  };

  // Handle modal OK based on action
  const handleSuccessOk = () => {
    const { action } = modalState;
    setModalState((prev) => ({ ...prev, open: false }));

    if (action === "deleteConfirm") {
      executeDelete();
    } else if (action === "deleteSuccess") {
      navigate("/user/Admission-Cycle-Config");
    } else if (action === "fetchError") {
      navigate("/user/Admission-Cycle-Config");
    }
  };

  // Handle Back
  const handleBack = () => {
    navigate("/user/Admission-Cycle-Config");
  };

  // Just close modal, stay on same page
  const handleModalClose = () => {
    setModalState((prev) => ({ ...prev, open: false }));
     navigate("/user/Admission-Cycle-Config");
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          {translations.labels?.loading || "Loading..."}
        </div>
      </div>
    );
  }

  return (
    <>
      <PopupModal
        open={modalState.open}
        title={
          modalState.type === "success"
            ? "Success"
            : modalState.type || "Failed"
        }
        message={modalState.message}
        onOk={handleSuccessOk}
        onClose={handleModalClose}
      />

      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaClipboardList />
          </div>
          <h1 className={styles.headerTitle}>
            {translations.labels?.admissionCycleConfig || "Admission Cycle Config"}
          </h1>
        </div>

        {/* Action Buttons */}
        <div className={styles.actions}>
          <CustomButton
            type="button"
            variant="contained"
            className={styles.addButton}
            fullWidth={false}
            onClick={handleEdit}
          >
            {translations.labels?.edit || "EDIT"}
          </CustomButton>
          <CustomButton
            type="button"
            variant="contained"
            className={styles.deleteButton}
            fullWidth={false}
            onClick={handleDelete}
            loading={deleting}
            loadingText={translations.labels?.deleting || "Deleting..."}
          >
            {translations.labels?.delete || "DELETE"}
          </CustomButton>
        </div>

        {/* View Form - All fields disabled */}
        <div className="row">
          <div className="col-lg-4 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={translations.labels?.academicYear || "Academic Year"}
              name="academicYear"
              type="text"
              value={formData.academicYear}
              disabled
              readOnly
            />
          </div>

          <div className="col-lg-4 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={translations.labels?.admissionType || "Admission Type"}
              name="admissionType"
              type="text"
              value={formData.admissionType}
              disabled
              readOnly
            />
          </div>

          <div className="col-lg-4 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={translations.labels?.admissionName || "Admission Name"}
              name="admissionName"
              type="text"
              value={formData.admissionName}
              disabled
              readOnly
            />
          </div>

          <div className="col-lg-4 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={translations.labels?.admissionStartDate || "Admission Start Date"}
              name="admissionStartDate"
              type="text"
              value={formData.admissionStartDate}
              disabled
              readOnly
            />
          </div>

          <div className="col-lg-4 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={translations.labels?.admissionEndDate || "Admission End Date"}
              name="admissionEndDate"
              type="text"
              value={formData.admissionEndDate}
              disabled
              readOnly
            />
          </div>
        </div>

        <div className="row">
          <div className="col-lg-4 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={translations.labels?.screeningStartDate || "Screening Start Date"}
              name="screeningStartDate"
              type="text"
              value={formData.screeningStartDate}
              disabled
              readOnly
            />
          </div>

          <div className="col-lg-4 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={translations.labels?.screeningEndDate || "Screening End Date"}
              name="screeningEndDate"
              type="text"
              value={formData.screeningEndDate}
              disabled
              readOnly
            />
          </div>
        </div>

        <div className="row">
          <div className="col-lg-4 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={translations.labels?.interviewStartDate || "Interview Start Date"}
              name="interviewStartDate"
              type="text"
              value={formData.interviewStartDate}
              disabled
              readOnly
            />
          </div>

          <div className="col-lg-4 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={translations.labels?.interviewEndDate || "Interview End Date"}
              name="interviewEndDate"
              type="text"
              value={formData.interviewEndDate}
              disabled
              readOnly
            />
          </div>
        </div>

        <div className="row">
          <div className="col-lg-4 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={translations.labels?.completionDate || "Completion Date"}
              name="completionDate"
              type="text"
              value={formData.completionDate}
              disabled
              readOnly
            />
          </div>
        </div>

        {/* Back Button */}
        <div className={styles.buttonContainer}>
          <CustomButton
            type="button"
            variant="contained"
            className={styles.cancelButton}
            fullWidth={false}
            onClick={handleBack}
          >
            {translations.labels?.back || "BACK"}
          </CustomButton>
        </div>
      </div>
    </>
  );
};

export default AdmissionCycleConfigView;
